import {ChangeDetectorRef, Component, EventEmitter, HostBinding, Inject, OnInit, Output} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MatButtonToggleChange} from '@angular/material/button-toggle';
import {MatDatepickerInputEvent} from '@angular/material/datepicker';
import {MatSlideToggleChange} from '@angular/material/slide-toggle';
import {Router} from '@angular/router';
import {NavigationFunctionData} from '@application/helper/navigation-helper/navigation-function-data.interface';
import {NavigationHelperService} from '@application/helper/navigation-helper/navigation-helper.service';
import {NavigationNewItemData} from '@application/helper/navigation-helper/navigation-new-item-data.interface';
import {RouteUtils} from '@application/helper/routing/route-utils';
import {MachineType} from '@domain/machine/machine-type.enum';
import {PlanningCreel} from '@domain/planning/carpet/planning-creel';
import {PlanningMachineCarpetRun} from '@domain/planning/carpet/planning-machine-carpet-run';
import {PlanningMachineCreelChange} from '@domain/planning/carpet/planning-machine-creel-change';
import {PlanningMachineQuality} from '@domain/planning/carpet/planning-machine-quality';
import {PlanningItem} from '@domain/planning/planning-item';
import {PlanningItemType} from '@domain/planning/planning-item-type.enum';
import {PlanningMachineMaintenance} from '@domain/planning/planning-machine-maintenance';
import {PlanningMachineRun} from '@domain/planning/planning-machine-run';
import {PlanningMachineUnavailability} from '@domain/planning/planning-machine-unavailability';
import {PlanningOrder} from '@domain/planning/planning-order';
import {PlanningMachineTuftingRun} from '@domain/planning/tufting/planning-machine-tufting-run';
import {PlanningTuftProduct} from '@domain/planning/tufting/planning-tuft-product';
import {PlanningMachineWarpChange} from '@domain/planning/weaving/planning-machine-warp-change';
import {PlanningMachineWeavingRun} from '@domain/planning/weaving/planning-machine-weaving-run';
import {PlanningWeaveProduct} from '@domain/planning/weaving/planning-weave-product';
import {Permission} from '@domain/profile/permission.enum';
import {Subscription} from '@domain/profile/subscription';
import {CreelMap} from '@domain/textile-data/creel/creel-map';
import {Authentication, AUTHENTICATION} from '@infrastructure/http/authentication/authentication';
import {CREELS, Creels} from '@infrastructure/http/creel/creels';
import {GetAllMachineQualitiesParameters, MACHINE_QUALITIES, MachineQualities} from '@infrastructure/http/machine-quality/machine-qualities';
import {PLANNING, Planning} from '@infrastructure/http/planning/planning';
import {HttpTuftProductsService} from '@infrastructure/http/tuft-product/http-tuft-products.service';
import {GetAllTuftProductsParameters} from '@infrastructure/http/tuft-product/tuft-products.interface';
import {WEAVE_PRODUCT, WeaveProducts} from '@infrastructure/http/weave-product/weave-products.interface';
import {PreviewMap} from '@presentation/components/creel-tables/preview-map';
import {SelectCreelComponent} from '@presentation/components/select-creel/select-creel.component';
import {SelectMachineQualityComponent} from '@presentation/components/select-machine-quality/select-machine-quality.component';
import {SelectWeaveProductComponent} from '@presentation/pages/texfab/production-order-lite/add/select-weave-product/select-weave-product.component';
import {SelectTuftProductComponent} from '@presentation/pages/texfab/tuft-production-order/add/select-tuft-product/select-tuft-product.component';
import {PreviewType} from '@presentation/pages/textile-data/creel/add/preview-type';
import {OverviewListCreel} from '@presentation/pages/textile-data/creel/overview/overview-list-creel';
import {OverviewListMachineQuality} from '@presentation/pages/textile-data/machine-quality/overview/overview-list-machine-quality';
import {OverviewListTuftProduct} from '@presentation/pages/textile-data/tuft-product/overview/overview-list-tuft-product';
import {OverviewListWeaveProduct} from '@presentation/pages/textile-data/weave-product/overview/overview-list-weave-product';
import {
  AgGridRowSelection,
  AssertionUtils,
  DateTimeFormat,
  DateTimePipe,
  DialogBuilderFactoryService,
  DialogComponentData,
  EnumUtils,
  skeletonViewAnimation,
  TimeUtils,
  TimezoneUtils,
  TranslateService
} from '@vdw/angular-component-library';
import {cloneDeep, first, isEmpty, last, toLower} from 'lodash-es';
import {DateTime} from 'luxon';
import {Subscription as RxjsSubscription} from 'rxjs';
import {delay, distinctUntilChanged, finalize, takeUntil} from 'rxjs/operators';
import {BasePlanningItemComponent} from '../base-planning-item.component';

@Component({
  selector: 'app-planning-item-form',
  templateUrl: './planning-item-form.component.html',
  styleUrls: ['./planning-item-form.component.scss'],
  animations: [skeletonViewAnimation('.form-field-skeleton-wrapper, .button-skeleton-wrapper, .ag-root')]
})
export class PlanningItemFormComponent extends BasePlanningItemComponent implements OnInit {
  private static readonly selectObjectTranslationKey = 'GENERAL.ACTIONS.SELECT_OBJECT';
  public readonly QUALITY_CHIP_VALUE_KEY = '%(technicalNameWithVersion)';
  public readonly CREEL_BASE_ROUTER_LINK = RouteUtils.paths.texStyle.creel.editCreel.absolutePath;
  public readonly QUALITY_BASE_ROUTER_LINK = RouteUtils.paths.texStyle.machineQuality.editMachineQuality.absolutePath;
  public readonly WEAVE_PRODUCT_BASE_ROUTER_LINK = RouteUtils.paths.texStyle.weaveProduct.editWeaveProduct.absolutePath;
  public readonly TUFT_PRODUCT_BASE_ROUTER_LINK = RouteUtils.paths.texStyle.tuftProduct.editTuftProduct.absolutePath;

  private readonly CARPET_QUEUE_PERMISSION = Permission.TEXFAB_CARPET_QUEUE;
  private readonly WEAVE_QUEUE_PERMISSION = Permission.TEXFAB_WEAVE_QUEUE;
  private readonly TUFT_QUEUE_PERMISSION = Permission.TEXFAB_TUFT_QUEUE;

  @Output() public planningItemChange: EventEmitter<PlanningItem> = new EventEmitter<PlanningItem>();
  @Output() public savePlanningItem: EventEmitter<PlanningItem> = new EventEmitter<PlanningItem>();

  @HostBinding('class.large-mode') public get largeMode(): boolean {
    return this.isPlanningItemOfTypeRun();
  }

  public planningItemForm: FormGroup<{
    type: FormControl<PlanningItemType>;
    startDate: FormControl<DateTime>;
    startTime: FormControl<string>;
    endDate: FormControl<DateTime>;
    endTime: FormControl<string>;
    planningOrders?: FormControl<PlanningOrder[]>;
    creel?: FormControl<OverviewListCreel>;
    machineQuality?: FormControl<PlanningMachineQuality>;
    weaveProduct?: FormControl<PlanningWeaveProduct>;
    tuftProduct?: FormControl<PlanningTuftProduct>;
  }>;

  public creelPreviewMap: PreviewMap = PreviewMap.THEORETICAL;
  public creelPreviewType: PreviewType = PreviewType.COLOR;
  public isEditingCreelMapping = false;
  public isLoadingCreel = false;

  public readonly planningItemTypes: PlanningItemType[] = EnumUtils.getEnumValues(PlanningItemType);

  private currentSubscription: Subscription;
  private getCreelSubscription: RxjsSubscription;
  private newCreel: OverviewListCreel;

  public constructor(
    @Inject(CREELS) private readonly creelsService: Creels,
    @Inject(PLANNING) private readonly planning: Planning,
    @Inject(MACHINE_QUALITIES) private readonly qualities: MachineQualities,
    @Inject(WEAVE_PRODUCT) private readonly weaveProducts: WeaveProducts,
    @Inject(AUTHENTICATION) protected readonly authentication: Authentication,
    private readonly tuftProducts: HttpTuftProductsService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly formBuilder: FormBuilder,
    private readonly translate: TranslateService,
    private readonly navigationHelperService: NavigationHelperService<NavigationNewItemData & DialogComponentData>,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly dateTimePipe: DateTimePipe,
    router: Router
  ) {
    super(authentication, router);
  }

  public ngOnInit(): void {
    this.currentSubscription = this.authentication.getCurrentSubscription();

    this.setPlanningItemForm();
    this.updateGenericPlanningItemFormValues();

    if (this.isPlanningItemOfTypeRun()) {
      this.updateRunSpecificFormValues();
    } else {
      this.updatePlanningItem();
    }

    this.planningItemSelected
      .pipe(
        takeUntil(this.unSubscribeOnViewDestroy),
        distinctUntilChanged((prev: PlanningItem, current: PlanningItem) => prev?.uniqueId === current?.uniqueId),
        delay(100)
      )
      .subscribe((_: PlanningItem) => {
        if (this.previousPlanningItem == null) {
          return;
        }

        this.updateGenericPlanningItemFormValues();

        this.cancelGetCreelRequest();
        this.closeEditCreelMapping();
        this.removeRunSpecificFormControls();

        if (this.isPlanningItemOfTypeRun()) {
          this.resetCreelPreviewTypeAndPreviewMap();
          this.addRunSpecificFormControls();
          this.updateRunSpecificFormValues();
        }
      });
  }

  public getAction(): string {
    const planning = this.translate.instant('PLANNING.PLANNING', {count: 1});
    const addToObject = this.translate.instant('GENERAL.ACTIONS.ADD_TO_OBJECT', {object: planning});

    const planningItemTypeLabel = this.translate.instant(this.getPlanningItemTypeLabel(this.planningItem.type));
    const editObject = this.translate.instant('GENERAL.ACTIONS.EDIT_OBJECT', {object: planningItemTypeLabel.toLowerCase()});

    return this.isAddingPlanningItem() ? addToObject : editObject;
  }

  public getTitle(): string {
    return this.isAddingPlanningItem() ? '' : this.machine.name;
  }

  public isAddingPlanningItem(): boolean {
    return AssertionUtils.isNullOrUndefined(this.planningItem.id);
  }

  public getPlanningItemTypeLabel(planningItemType: PlanningItemType): string {
    return `PLANNING.ADD_ITEM.TYPES.${PlanningItemType[planningItemType]}`;
  }

  public updateStartTimeWhenInThePast(): void {
    const startTimeInMs = this.getMillisecondsFromTimeLabel(this.planningItemForm.getRawValue().startTime);
    const startDateWithStartTime: DateTime = this.planningItemForm.getRawValue().startDate.plus({milliseconds: startTimeInMs});
    const currentDate: Date = this.getCurrentDateForSubscriptionTimezone();

    if (startDateWithStartTime < DateTime.fromJSDate(currentDate)) {
      this.planningItemForm.controls.startTime.setValue(this.getTimeLabelFromDate(currentDate));
    }

    if (this.isPlanningItemOfTypeRun()) {
      this.reschedulePlanningOrders(this.getPlanningOrders());
    }

    this.updateEndTimeAndEndDate();
  }

  public updateEndTimeAndEndDate(): void {
    const {startDate, endDate} = this.getPlanningItemStartAndEndDate();

    const minimumAllowedDifferenceBetweenStartDateAndEndDateInMs = this.getMinimumAllowedDifferenceBetweenStartDateAndEndDateInMs();

    if (minimumAllowedDifferenceBetweenStartDateAndEndDateInMs > DateTime.fromJSDate(endDate).diff(DateTime.fromJSDate(startDate)).milliseconds) {
      const calculatedEndDate = DateTime.fromJSDate(startDate).plus({milliseconds: minimumAllowedDifferenceBetweenStartDateAndEndDateInMs});
      this.planningItemForm.controls.endTime.setValue(this.getTimeLabelFromDate(calculatedEndDate.toJSDate()));
      this.planningItemForm.controls.endDate.setValue(calculatedEndDate.startOf('day'));
    }

    this.updatePlanningItem();
  }

  public canShowPlanningItemType(planningItemType: PlanningItemType): boolean {
    if (!this.isAddingPlanningItem() && planningItemType === PlanningItemType.RUN) {
      return false;
    }
    const warpingMachines = [MachineType.WEAVING_MACHINE, MachineType.TUFT];
    if (planningItemType === PlanningItemType.CREEL_CHANGE) {
      return !warpingMachines.includes(this.machine.machineType);
    }
    if (planningItemType === PlanningItemType.WARP_CHANGE) {
      return warpingMachines.includes(this.machine.machineType);
    }

    return true;
  }

  public canShowRunSpecificFormControls(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.planningItemForm.controls.planningOrders);
  }

  public canShowCarpetSpecificControls(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.planningItemForm.controls.machineQuality) && !AssertionUtils.isNullOrUndefined(this.planningItemForm.controls.creel);
  }

  public canShowWeavingSpecificControls(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.planningItemForm.controls.weaveProduct);
  }

  public canShowTuftingSpecificControls(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.planningItemForm.controls.tuftProduct);
  }

  public isMachineQualitySelected(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.planningItemForm.getRawValue()?.machineQuality);
  }

  public isCreelSelected(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.planningItemForm.getRawValue()?.creel);
  }

  public isWeaveProductSelected(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.planningItemForm.getRawValue()?.weaveProduct);
  }

  public isTuftProductSelected(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.planningItemForm.getRawValue()?.tuftProduct);
  }

  public canQueueProductionOrder(): boolean {
    if (!this.isPlanningItemOfTypeRun()) {
      return this.canEditForMachineType(this.machine.machineType);
    } else if (this.isPlanningItemCarpetRun()) {
      return this.currentSubscription?.hasPermission(this.CARPET_QUEUE_PERMISSION);
    } else if (this.isPlanningItemWeavingRun()) {
      return this.currentSubscription?.hasPermission(this.WEAVE_QUEUE_PERMISSION);
    } else if (this.isPlanningItemTuftingRun()) {
      return this.currentSubscription?.hasPermission(this.TUFT_QUEUE_PERMISSION);
    }

    return false;
  }

  public selectMachineQuality(): void {
    this.dialogBuilderFactoryService
      .getBuilder()
      .openSelectGridDialog(SelectMachineQualityComponent, {
        title: this.translate.instant(PlanningItemFormComponent.selectObjectTranslationKey, {
          object: toLower(this.translate.instant('TEXTILE_DATA.MACHINE_QUALITY.MACHINE_QUALITY', {count: 1}))
        }),
        rowSelection: AgGridRowSelection.SINGLE,
        machineId: this.machine.id,
        allowEmptySelection: false
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((selectedQualities: OverviewListMachineQuality[]) => {
        if (!isEmpty(selectedQualities)) {
          this.planningItemForm.controls.machineQuality.setValue(PlanningMachineQuality.fromOverviewListMachineQuality(first(selectedQualities)));
          this.updatePlanningItem();
        }
      });
  }

  public selectCreel(): void {
    this.dialogBuilderFactoryService
      .getBuilder()
      .openSelectGridDialog(SelectCreelComponent, {
        title: this.translate.instant(PlanningItemFormComponent.selectObjectTranslationKey, {
          object: toLower(this.translate.instant('TEXTILE_DATA.CREEL.CREEL', {count: 1}))
        }),
        machineId: this.machine.id,
        allowEmptySelection: false
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((selectedCreels: OverviewListCreel[]) => this.setSelectedCreels(selectedCreels));
  }

  public selectWeaveProduct(): void {
    this.dialogBuilderFactoryService
      .getBuilder()
      .openSelectGridDialog(SelectWeaveProductComponent, {
        title: this.translate.instant(PlanningItemFormComponent.selectObjectTranslationKey, {
          object: this.translate.instant('TEXTILE_DATA.WEAVE_PRODUCT.WEAVE_PRODUCT', {count: 1}).toLowerCase()
        }),
        rowSelection: AgGridRowSelection.SINGLE,
        machineId: this.machine.id,
        allowEmptySelection: false
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((selectedWeaveProduct: OverviewListWeaveProduct | '') => {
        if (!AssertionUtils.isNullOrUndefined(selectedWeaveProduct) && selectedWeaveProduct !== '') {
          this.planningItemForm.controls.weaveProduct.setValue(PlanningWeaveProduct.fromOverviewListWeaveProduct(selectedWeaveProduct));
          this.updatePlanningItem();
        }
      });
  }

  public selectTuftProduct(): void {
    this.dialogBuilderFactoryService
      .getBuilder()
      .openSelectGridDialog(SelectTuftProductComponent, {
        title: this.translate.instant(PlanningItemFormComponent.selectObjectTranslationKey, {
          object: this.translate.instant('TEXTILE_DATA.TUFT_PRODUCT.TUFT_PRODUCT', {count: 1}).toLowerCase()
        }),
        rowSelection: AgGridRowSelection.SINGLE,
        machineId: this.machine.id,
        allowEmptySelection: false
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((selectedTuftProduct: OverviewListTuftProduct | '') => {
        if (!AssertionUtils.isNullOrUndefined(selectedTuftProduct) && selectedTuftProduct !== '') {
          this.planningItemForm.controls.tuftProduct.setValue(PlanningTuftProduct.fromOverviewListTuftProduct(selectedTuftProduct));
          this.updatePlanningItem();
        }
      });
  }

  public save(): void {
    this.savePlanningItem.emit(this.getPlanningItem());
  }

  public onCreelTypeViewModeChange(event: MatSlideToggleChange): void {
    this.creelPreviewType = event.checked ? PreviewType.YARN_TYPE : PreviewType.COLOR;
  }

  public onCreelMapViewModeChange(matButtonToggleChange: MatButtonToggleChange): void {
    this.creelPreviewMap = matButtonToggleChange.value ? PreviewMap.THEORETICAL : PreviewMap.ACTIVE;
  }

  public editCreelMapping(): void {
    this.isEditingCreelMapping = true;
  }

  public closeEditCreelMapping(): void {
    this.isEditingCreelMapping = false;
  }

  public updateCreelMapping(mappingForCurrentPositionOnMachine: CreelMap[]): void {
    const creel = this.planningItemForm.getRawValue().creel;
    creel.mappingForCurrentPositionOnMachine = mappingForCurrentPositionOnMachine;
    this.planningItemForm.controls.creel.patchValue(creel);

    this.creelPreviewMap = PreviewMap.ACTIVE;

    this.closeEditCreelMapping();
    this.planningItemChange.emit(this.getPlanningItem());
  }

  public canChangePlanningItemType(): boolean {
    return this.isAddingPlanningItem() || (!this.isPlanningItemOfTypeRun() && this.isPlanningItemStartDateAfterCurrentDate());
  }

  public updatePlanningItem(): void {
    const newPlanningItem = this.getPlanningItem();
    this.planningItemChange.emit(newPlanningItem);

    if (newPlanningItem instanceof PlanningMachineRun) {
      if ((newPlanningItem as PlanningMachineCarpetRun).planningOrders?.length === 0) {
        this.planningItemForm.controls.weaveProduct?.enable();
        this.planningItemForm.controls.tuftProduct?.enable();
        this.planningItemForm.controls.creel?.enable();
        this.planningItemForm.controls.machineQuality?.enable();
      } else {
        this.planningItemForm.controls.weaveProduct?.disable();
        this.planningItemForm.controls.tuftProduct?.disable();
        this.planningItemForm.controls.creel?.disable();
        this.planningItemForm.controls.machineQuality?.disable();
      }
    }
  }

  public canChangeRunProperties(): boolean {
    return this.isPlanningItemOfTypeRun() && !(this.planningItem as PlanningMachineRun).hasPlanningOrders;
  }

  public isCurrentPreviewModeActiveCreel(): boolean {
    return this.creelPreviewMap === PreviewMap.ACTIVE;
  }

  public isCurrentPreviewModeYarnType(): boolean {
    return this.creelPreviewType === PreviewType.YARN_TYPE;
  }

  public getMinDateForStartDate(): Date {
    return DateTime.fromJSDate(this.getCurrentDateForSubscriptionTimezone()).startOf('day').toJSDate();
  }

  public getMinDateForEndDate(): Date {
    const {startDate}: {startDate: Date} = this.getPlanningItemStartAndEndDate();
    const minimumAllowedDifferenceBetweenStartDateAndEndDateInMs = this.getMinimumAllowedDifferenceBetweenStartDateAndEndDateInMs();

    return DateTime.fromJSDate(startDate).plus({milliseconds: minimumAllowedDifferenceBetweenStartDateAndEndDateInMs}).startOf('day').toJSDate();
  }

  public onStartDateChange(event: MatDatepickerInputEvent<DateTime>): void {
    const newStartDate: DateTime = event.value;
    const currentStartDate: Date = DateTime.fromJSDate(this.planningItem.startDate).startOf('day').toJSDate();
    const minDateForStartDate = this.getMinDateForStartDate();
    const currentStartDateDateTime = DateTime.fromJSDate(currentStartDate);

    if (!AssertionUtils.isNullOrUndefined(newStartDate) && newStartDate >= DateTime.fromJSDate(minDateForStartDate) && currentStartDateDateTime !== newStartDate) {
      const endDate = this.planningItemForm.value.endDate;
      if (newStartDate > endDate) {
        this.planningItemForm.controls.endDate.setValue(newStartDate);
      }

      this.updateStartTimeWhenInThePast();
    } else if (AssertionUtils.isNullOrUndefined(newStartDate) || newStartDate < DateTime.fromJSDate(minDateForStartDate)) {
      this.planningItemForm.controls.startDate.setValue(currentStartDateDateTime);
    }
  }

  public onEndDateChange(event: MatDatepickerInputEvent<DateTime>): void {
    const newEndDate: DateTime = event.value;
    const currentEndDate: Date = DateTime.fromJSDate(this.planningItem.endDate).startOf('day').toJSDate();
    const minDateForEndDate = this.getMinDateForEndDate();
    const currentEndDateDateTime = DateTime.fromJSDate(currentEndDate);

    if (!AssertionUtils.isNullOrUndefined(newEndDate) && newEndDate >= DateTime.fromJSDate(minDateForEndDate) && currentEndDateDateTime !== newEndDate) {
      const {startDate, endDate} = this.getPlanningItemStartAndEndDate();

      const minimumAllowedDifferenceBetweenStartDateAndEndDateInMs = this.getMinimumAllowedDifferenceBetweenStartDateAndEndDateInMs();

      if (minimumAllowedDifferenceBetweenStartDateAndEndDateInMs > DateTime.fromJSDate(endDate).diff(DateTime.fromJSDate(startDate)).milliseconds) {
        const endTime = this.getTimeLabelFromDate(DateTime.fromJSDate(startDate).plus({milliseconds: minimumAllowedDifferenceBetweenStartDateAndEndDateInMs}).toJSDate());

        this.planningItemForm.controls.endTime.setValue(endTime);
      }

      this.updatePlanningItem();
    } else if (AssertionUtils.isNullOrUndefined(newEndDate) || newEndDate < DateTime.fromJSDate(minDateForEndDate)) {
      this.planningItemForm.controls.endDate.setValue(currentEndDateDateTime);
    }
  }

  public deletePlanningOrder(planningOrder: PlanningOrder): void {
    const planningOrders: PlanningOrder[] = this.getPlanningOrders().filter((order: PlanningOrder) => order.productionOrder.id !== planningOrder.productionOrder.id);

    this.reschedulePlanningOrders(planningOrders);
    this.updatePlanningItem();
  }

  public canShowProductionOrdersList(): boolean {
    return (
      (this.isPlanningItemCarpetRun() && this.isMachineQualitySelected() && this.isCreelSelected()) ||
      (this.isPlanningItemWeavingRun() && this.isWeaveProductSelected()) ||
      (this.isPlanningItemTuftingRun() && this.isTuftProductSelected())
    );
  }

  public addPlanningOrders(planningOrders: PlanningOrder[]): void {
    this.reschedulePlanningOrders([...this.getPlanningOrders(), ...planningOrders]);

    const {endDate} = this.getPlanningItemStartAndEndDate();
    const lastOrder = last(planningOrders);
    const lastendDate = DateTime.fromJSDate(lastOrder.endDate);

    if (DateTime.fromJSDate(endDate) < lastendDate) {
      this.planningItemForm.controls.endTime.setValue(this.getTimeLabelFromDate(lastOrder.endDate));
      this.planningItemForm.controls.endDate.setValue(lastendDate.startOf('day'));
    }

    this.updatePlanningItem();
  }

  public updatePlanningOrders(planningOrders: PlanningOrder[]): void {
    this.reschedulePlanningOrders(planningOrders);

    this.updatePlanningItem();
  }

  private setPlanningItemForm(): void {
    this.planningItemForm = this.formBuilder.group({
      type: this.formBuilder.control(PlanningItemType.MAINTENANCE, Validators.required),
      startDate: this.formBuilder.control(null, Validators.required),
      startTime: this.formBuilder.control(null, Validators.required),
      endDate: this.formBuilder.control(null, Validators.required),
      endTime: this.formBuilder.control(null, Validators.required)
    });

    if (this.isPlanningItemOfTypeRun()) {
      this.addRunSpecificFormControls();
    }
  }

  private getTimeLabelFromDate(date: Date): string {
    return this.dateTimePipe.transform(date, DateTimeFormat.TIME_SIMPLE);
  }

  private updateGenericPlanningItemFormValues(): void {
    const startDate = DateTime.fromJSDate(this.planningItem.startDate);
    const startTime = this.getTimeLabelFromDate(this.planningItem.startDate);

    const endDate = DateTime.fromJSDate(this.planningItem.endDate);
    const endTime = this.getTimeLabelFromDate(this.planningItem.endDate);

    if (this.planningItemForm) {
      this.planningItemForm.controls.type.setValue(this.planningItem.type);
      this.planningItemForm.controls.startDate.setValue(startDate.startOf('day'));
      this.planningItemForm.controls.startTime.setValue(startTime);
      this.planningItemForm.controls.endDate.setValue(endDate.startOf('day'));
      this.planningItemForm.controls.endTime.setValue(endTime);
    }

    if (this.canChangePlanningItemStartDate()) {
      this.planningItemForm.controls.startDate.enable();
      this.planningItemForm.controls.startTime.enable();
    } else {
      this.planningItemForm.controls.startDate.disable();
      this.planningItemForm.controls.startTime.disable();
    }

    if (!this.canEditForMachineType(this.machine.machineType)) {
      this.planningItemForm.disable();
    }
  }

  private canEditForMachineType(machineType: MachineType): boolean {
    if (this.planningItem instanceof PlanningMachineCarpetRun) {
      const planningCreel = this.planningItem.creel;
      this.creelsService
        .getPossibleCreelsForMachine(this.machine.id)
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe((creels: OverviewListCreel[]) => {
          this.setSelectedCreels([creels.find((creel: OverviewListCreel) => creel.id === planningCreel?.id)], planningCreel?.mappingForCurrentPositionOnMachine);
        });
      this.planningItemForm.controls.machineQuality?.setValue(this.planningItem.machineQuality);
    }

    switch (machineType) {
      case MachineType.CARPET_LOOM:
      case MachineType.VELVET:
        return this.currentSubscription?.hasPermission(this.CARPET_QUEUE_PERMISSION);
      case MachineType.TUFT:
        return this.currentSubscription?.hasPermission(this.TUFT_QUEUE_PERMISSION);
      case MachineType.WEAVING_MACHINE:
        return this.currentSubscription?.hasPermission(this.WEAVE_QUEUE_PERMISSION);
      default:
        return true;
    }
  }

  private updateRunSpecificFormValues(): void {
    const run = this.planningItem as PlanningMachineRun;
    this.planningItemForm.controls.planningOrders.setValue(cloneDeep(run.planningOrders));
    if (this.machine.machineType === MachineType.WEAVING_MACHINE) {
      this.updateWeavingRunSpecificFormValues(run as PlanningMachineWeavingRun);
    } else if (this.machine.machineType === MachineType.TUFT) {
      this.updateTuftingRunSpecificFormValues(run as PlanningMachineTuftingRun);
    } else {
      this.updateCarpetRunSpecificFormValues(run as PlanningMachineCarpetRun);
    }
  }

  private updateCarpetRunSpecificFormValues(run: PlanningMachineCarpetRun): void {
    this.planningItemForm.controls.machineQuality.setValue(run.machineQuality);
    this.planningItemForm.controls.creel.setValue(null);

    if (!AssertionUtils.isNullOrUndefined(run.creel) && !AssertionUtils.isNullOrUndefined(this.planningItem.id)) {
      if (this.newCreel) {
        this.setSelectedCreels([this.newCreel]);
        this.newCreel = null;
      } else {
        this.isLoadingCreel = true;
        this.getCreelSubscription = this.planning
          .getCreelForRun(this.planningItem.id)
          .pipe(
            finalize(() => (this.isLoadingCreel = false)),
            takeUntil(this.unSubscribeOnViewDestroy)
          )
          .subscribe((creel: OverviewListCreel) => {
            if (run?.creel?.mappingForCurrentPositionOnMachine) {
              creel.mappingForCurrentPositionOnMachine = run.creel.mappingForCurrentPositionOnMachine;
            } else if (AssertionUtils.isNullOrUndefined(creel.mappingForCurrentPositionOnMachine)) {
              creel.mappingForCurrentPositionOnMachine = creel.getOriginalMappingForCurrentPositionOnMachine();
            }

            this.planningItemForm.controls.creel.setValue(creel);

            this.updatePlanningItem();
          });
      }
    } else {
      this.updatePlanningItem();
    }

    const getFunctionsBasedOnDialogComponent = new Map<any, NavigationFunctionData>([
      [SelectCreelComponent, {loadNewItem: ({newItemId}: NavigationNewItemData): void => this.setCreel(newItemId), reopenDialog: (): void => this.selectCreel()}],
      [SelectMachineQualityComponent, {loadNewItem: ({newItemId}: NavigationNewItemData): void => this.setQuality(newItemId), reopenDialog: (): void => this.selectMachineQuality()}]
    ]);

    this.navigationHelperService.setNewItemOrReopenDialogIfPresent(
      ({newItemId}: NavigationNewItemData, {dialogComponent}: DialogComponentData) => getFunctionsBasedOnDialogComponent.get(dialogComponent).loadNewItem({newItemId}),
      ({dialogComponent}: DialogComponentData) => getFunctionsBasedOnDialogComponent.get(dialogComponent).reopenDialog()
    );
  }

  private updateWeavingRunSpecificFormValues(run: PlanningMachineWeavingRun): void {
    this.planningItemForm.controls.weaveProduct.setValue(run.weaveProduct);
    this.navigationHelperService.setNewItemOrReopenDialogIfPresent(
      ({newItemId}: NavigationNewItemData) => this.setWeaveProduct(newItemId),
      () => this.selectWeaveProduct()
    );
  }

  private updateTuftingRunSpecificFormValues(run: PlanningMachineTuftingRun): void {
    this.planningItemForm.controls.tuftProduct.setValue(run.tuftProduct);
    this.navigationHelperService.setNewItemOrReopenDialogIfPresent(
      ({newItemId}: NavigationNewItemData) => this.setTuftProduct(newItemId),
      () => this.selectTuftProduct()
    );
  }

  private getMillisecondsFromTimeLabel(timeLabel: string): number {
    const time = DateTime.fromFormat(timeLabel, timeLabel.includes('M') ? 'h:mm a' : 'H:mm');

    return Number(time.hour) * TimeUtils.HOUR_IN_MS + Number(time.minute) * TimeUtils.MINUTE_IN_MS;
  }

  private getPlanningItemStartAndEndDate(): {startDate: Date; endDate: Date} {
    let startDate: Date;
    if (this.planningItemForm.controls.startTime.disabled && this.planningItemForm.controls.startDate.disabled) {
      startDate = this.planningItem.startDate;
    } else {
      const startTimeInMs: number = this.getMillisecondsFromTimeLabel(this.planningItemForm.getRawValue().startTime);
      startDate = this.planningItemForm.getRawValue().startDate.plus({milliseconds: startTimeInMs}).toJSDate();
    }

    const endTimeInMs: number = this.getMillisecondsFromTimeLabel(this.planningItemForm.value.endTime);
    const endDate = this.planningItemForm.value.endDate?.plus({milliseconds: endTimeInMs}).toJSDate();

    return {startDate, endDate};
  }

  private getPlanningItem(): PlanningItem {
    const type = this.planningItemForm.value.type;
    let planningItem: PlanningItem;

    const {startDate, endDate}: {startDate: Date; endDate: Date} = this.getPlanningItemStartAndEndDate();

    if (type === PlanningItemType.RUN) {
      let planningOrders: PlanningOrder[] = [];
      let compatibleWithMachine = true;
      let creel: OverviewListCreel = null;
      let machineQuality: PlanningMachineQuality = null;
      let weaveProduct: PlanningWeaveProduct = null;
      let tuftProduct: PlanningTuftProduct = null;

      if (this.isPlanningItemOfTypeRun()) {
        ({compatibleWithMachine, planningOrders, creel, machineQuality, weaveProduct, tuftProduct} = this.getRunPlanningItemProperties({creel, machineQuality, weaveProduct, tuftProduct}));
      }

      if (this.machine.machineType === MachineType.WEAVING_MACHINE) {
        planningItem = new PlanningMachineWeavingRun(this.planningItem.id, weaveProduct, planningOrders, null, null, compatibleWithMachine);
      } else if (this.machine.machineType === MachineType.TUFT) {
        planningItem = new PlanningMachineTuftingRun(this.planningItem.id, tuftProduct, planningOrders, null, null, compatibleWithMachine);
      } else {
        planningItem = new PlanningMachineCarpetRun(
          this.planningItem.id,
          machineQuality,
          AssertionUtils.isNullOrUndefined(creel) ? null : PlanningCreel.fromOverviewListCreel(creel),
          planningOrders,
          null,
          null,
          compatibleWithMachine
        );
      }

      planningItem.reschedule(startDate, endDate);
    } else if (type === PlanningItemType.MAINTENANCE) {
      planningItem = new PlanningMachineMaintenance(this.planningItem.id, startDate, endDate);
    } else if (type === PlanningItemType.CREEL_CHANGE) {
      planningItem = new PlanningMachineCreelChange(this.planningItem.id, startDate, endDate);
    } else if (type === PlanningItemType.MACHINE_UNAVAILABLE) {
      planningItem = new PlanningMachineUnavailability(this.planningItem.id, startDate, endDate);
    } else if (type === PlanningItemType.WARP_CHANGE) {
      planningItem = new PlanningMachineWarpChange(this.planningItem.id, startDate, endDate);
    }

    return planningItem;
  }

  private setSelectedCreels(selectedCreels: OverviewListCreel[], mappingForCurrentPositionOnMachine: CreelMap[] = null): void {
    const newSelectedCreel = !isEmpty(selectedCreels) ? first(selectedCreels) : null;
    const currentSelectedCreel: OverviewListCreel = this.planningItemForm.getRawValue()?.creel;

    if (!AssertionUtils.isNullOrUndefined(newSelectedCreel) && (AssertionUtils.isNullOrUndefined(currentSelectedCreel) || newSelectedCreel.id !== currentSelectedCreel.id)) {
      this.resetCreelPreviewTypeAndPreviewMap();

      newSelectedCreel.mappingForCurrentPositionOnMachine = mappingForCurrentPositionOnMachine ? mappingForCurrentPositionOnMachine : newSelectedCreel.getOriginalMappingForCurrentPositionOnMachine();

      this.planningItemForm.controls.creel?.setValue(newSelectedCreel);

      this.updatePlanningItem();
    }
  }

  private getRunPlanningItemProperties({
    creel,
    machineQuality,
    weaveProduct,
    tuftProduct
  }: {
    creel: OverviewListCreel;
    machineQuality: PlanningMachineQuality;
    weaveProduct: PlanningWeaveProduct;
    tuftProduct: PlanningTuftProduct;
  }): {
    compatibleWithMachine: boolean;
    planningOrders: PlanningOrder[];
    creel: OverviewListCreel;
    machineQuality: PlanningMachineQuality;
    weaveProduct: PlanningWeaveProduct;
    tuftProduct: PlanningTuftProduct;
  } {
    const run = this.planningItem as PlanningMachineRun;
    const compatibleWithMachine = this.isAddingPlanningItem() || run.compatibleWithMachine;
    const planningOrders = this.planningItemForm.value?.planningOrders;
    if (run instanceof PlanningMachineCarpetRun) {
      creel = this.planningItemForm.getRawValue()?.creel;
      machineQuality = this.planningItemForm.getRawValue()?.machineQuality;
    } else if (run instanceof PlanningMachineWeavingRun) {
      weaveProduct = this.planningItemForm.getRawValue()?.weaveProduct;
    } else if (run instanceof PlanningMachineTuftingRun) {
      tuftProduct = this.planningItemForm.getRawValue()?.tuftProduct;
    }
    return {compatibleWithMachine, planningOrders, creel, machineQuality, weaveProduct, tuftProduct};
  }

  private addRunSpecificFormControls(): void {
    this.planningItemForm.addControl('planningOrders', this.formBuilder.control([]));
    if (this.machine.machineType === MachineType.WEAVING_MACHINE) {
      this.planningItemForm.addControl('weaveProduct', this.formBuilder.control({value: null, disabled: !this.canChangeRunProperties()}, Validators.required));
    } else if (this.machine.machineType === MachineType.TUFT) {
      this.planningItemForm.addControl('tuftProduct', this.formBuilder.control({value: null, disabled: !this.canChangeRunProperties()}, Validators.required));
    } else {
      this.planningItemForm.addControl('machineQuality', this.formBuilder.control({value: null, disabled: !this.canChangeRunProperties()}, Validators.required));
      this.planningItemForm.addControl('creel', this.formBuilder.control({value: null, disabled: !this.canChangeRunProperties()}, Validators.required));
    }
  }

  private removeRunSpecificFormControls(): void {
    this.planningItemForm.removeControl('planningOrders');
    this.planningItemForm.removeControl('machineQuality');
    this.planningItemForm.removeControl('creel');
    this.planningItemForm.removeControl('weaveProduct');
    this.planningItemForm.removeControl('tuftProduct');
    this.changeDetectorRef.detectChanges();
  }

  private resetCreelPreviewTypeAndPreviewMap(): void {
    this.creelPreviewMap = PreviewMap.THEORETICAL;
    this.creelPreviewType = PreviewType.COLOR;
  }

  private cancelGetCreelRequest(): void {
    if (!AssertionUtils.isNullOrUndefined(this.getCreelSubscription)) {
      this.getCreelSubscription.unsubscribe();
      this.getCreelSubscription = null;
    }
  }

  private getMinimumAllowedDifferenceBetweenStartDateAndEndDateInMs(): number {
    return this.planningItem instanceof PlanningMachineRun && this.planningItem.hasPlanningOrders
      ? DateTime.fromJSDate(last(this.planningItem.planningOrders).endDate).diff(DateTime.fromJSDate(this.planningItem.startDate)).milliseconds
      : TimeUtils.MINUTE_IN_MS;
  }

  private reschedulePlanningOrders(planningOrders: PlanningOrder[]): void {
    let {startDate} = this.getPlanningItemStartAndEndDate();
    for (const planningOrder of planningOrders) {
      planningOrder.updateStartWhilePreservingDuration(startDate);
      startDate = DateTime.fromJSDate(startDate).plus({milliseconds: planningOrder.totalRunningTimeInMs}).toJSDate();
    }

    this.planningItemForm.controls.planningOrders.setValue(planningOrders);
  }

  private getPlanningOrders(): PlanningOrder[] {
    return cloneDeep(this.planningItemForm.controls.planningOrders.value);
  }

  private getCurrentDateForSubscriptionTimezone(): Date {
    return TimezoneUtils.convertToDateWithCurrentOffset(new Date().toISOString(), this.authentication.getCurrentSubscription().timeZone.code);
  }

  private canChangePlanningItemStartDate(): boolean {
    return (this.planningItem instanceof PlanningMachineRun && !this.planningItem.executing) || (!(this.planningItem instanceof PlanningMachineRun) && this.isPlanningItemStartDateAfterCurrentDate());
  }

  private isPlanningItemStartDateAfterCurrentDate(): boolean {
    return DateTime.fromJSDate(this.planningItem.startDate) > DateTime.fromJSDate(this.getCurrentDateForSubscriptionTimezone());
  }

  private setCreel(id: number): void {
    this.creelsService
      .getPossibleCreelsForMachine(this.machine.id)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((creels: OverviewListCreel[]) => {
        this.newCreel = creels.find((creel: OverviewListCreel) => creel.id === id);
        this.setSelectedCreels([this.newCreel]);
      });
  }

  private setQuality(id: number): void {
    const parameters: Partial<GetAllMachineQualitiesParameters> = {showOnlyLatestVersion: true};
    this.qualities
      .getAll(parameters)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((qualities: OverviewListMachineQuality[]) => {
        this.planningItemForm.controls.machineQuality.setValue(PlanningMachineQuality.fromOverviewListMachineQuality(qualities.find((quality: OverviewListMachineQuality) => quality.id === id)));
        this.updatePlanningItem();
      });
  }

  private setWeaveProduct(id: number): void {
    this.weaveProducts
      .getAll()
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((weaveProducts: OverviewListWeaveProduct[]) => {
        this.planningItemForm.controls.weaveProduct.setValue(PlanningWeaveProduct.fromOverviewListWeaveProduct(weaveProducts.find((weaveProduct: OverviewListWeaveProduct) => weaveProduct.id === id)));
        this.updatePlanningItem();
      });
  }

  private setTuftProduct(id: number): void {
    const parameters: Partial<GetAllTuftProductsParameters> = {showOnlyLatestVersion: true};
    this.tuftProducts
      .getAllForMachineId(this.machine.id, parameters)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((tuftProducts: OverviewListTuftProduct[]) => {
        this.planningItemForm.controls.tuftProduct.setValue(PlanningTuftProduct.fromOverviewListTuftProduct(tuftProducts.find((tuftProduct: OverviewListTuftProduct) => tuftProduct.id === id)));
        this.updatePlanningItem();
      });
  }
}
